import * as Amplitude from '@amplitude/analytics-browser';
import { sha256 } from 'js-sha256';
import urlJoin from 'url-join';

import {
  BOOKING_CREATED,
  BOOKING_CREATED_ALL,
  CHILDCARE_PRESTATIONS_PAGE,
  PAYMENT_PAGE,
  SUMMARY_PAGE,
} from '@wecasa/common/src/constants/tracking';
import { SCREEN_VIEW } from '@wecasa/common/src/constants/tracking-events';
import type {
  CleaningPrestation,
  CleaningPrestationOption,
  ProChoiceSource,
  UniverseReference,
  User,
} from '@wecasa/common/src/types/commons';
import type { TrackLocationEvent } from '@wecasa/common/src/types/tracking';
import { isRecurringChildcare, isRecurringCleaning } from '@wecasa/common/src/utils/booking';
import { getCurrentCountry, getCurrentCurrency } from '@wecasa/common/src/utils/country';
import { bookingMetaData, composeEvent, composeEventByUniverse } from '@wecasa/common/src/utils/intercom';
import { formatLocaleToPreciseLocale } from '@wecasa/common/src/utils/locale';
import { toPascalCase } from '@wecasa/common/src/utils/strings';
import { isChildcareReference, isCleaningReference, isWellnessReference } from '@wecasa/common/src/utils/universes';

import type { BasketState, PayloadPotential } from '@wecasa/customer/src/ducks/types';
import type { BasketMode } from '@wecasa/customer/src/types/basket';
import type {
  CheckoutConfirmationEvent,
  SignInEvent,
  TrackingEventType as TrackingEventTypeCustomer,
} from '@wecasa/customer/src/types/tracking';
import { extractCleaningPrestationAndOptionsFromBooking } from '@wecasa/customer/src/utils/basket';
import { getSourcesFormatted } from '@wecasa/customer/src/utils/previous-pros';

import { getI18nLocale } from '@wecasa/i18n';

import type { MembershipEvent } from '@wecasa/membership/src/types/tracking';

import { getDeviceIdFromAmplitudeCookie } from '@wecasa/web-ui/src/utils/amplitude-experiment';

import {
  CHECKOUT_CONFIRMATION_PATH,
  CUSTOMER_PATH,
  type Path,
  PRESTATIONS_PATH,
  PREVIOUS_PROS_PATH,
  RECAP_PATH,
  ROUTES,
  route,
  SUMMARY_PATH,
} from '../steps/steps';

type TrackingEventType = TrackingEventTypeCustomer | MembershipEvent;

export const getGtmDataLayer = () => {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  return window.dataLayer;
};

export const trackingOptin = () => {
  Amplitude.setOptOut(false);
};

export const initializeTracking = (apiKey: string, trackingServerUrl: string) => {
  const dataLayer = getGtmDataLayer();

  Amplitude.init(apiKey, {
    defaultTracking: false,
    // read initial consent in datalayer when it's been set before react loads
    optOut: dataLayer.findIndex((e) => e.event === 'cookie_consent_marketing') === -1,
    serverUrl: urlJoin(trackingServerUrl, '/2/httpapi'),
  });

  Amplitude.setDeviceId(getDeviceIdFromAmplitudeCookie(apiKey));

  // The following code listens for CookieBot cookie_consent_marketing consent,
  // in case the user accepts it (cookie banner) after the react app is fired up

  // Inspired by https://github.com/Brady-Agranoff/dataLayer-detect/
  const originalPush = dataLayer.push.bind(dataLayer);

  // Replace Array.prototype.push with our own
  dataLayer.push = (obj) => {
    originalPush(obj);

    // user accepts marketing cookies in banner
    if (obj.event === 'cookie_consent_marketing') {
      trackingOptin();
    }

    return dataLayer.length;
  };
};

export const signInUser = (userPublicId: string) => {
  Amplitude.setUserId(userPublicId);
};

export const logoutUser = () => {
  Amplitude.setUserId(undefined);
};

const matchRouteWithURLParams = (pathname: string, localisedRouteName: string) => {
  /*
   * Let's take a URL `/foo/bar/`
   * - `replace` will remove trailing slash `/foo/bar/` -> `/foo/bar`
   * - `split` will split the pathname into an array of string ['', 'foo', 'bar']
   * - `filter` will remove empty strings
   * Result: ['foo', 'bar']
   */
  const routeSegments = pathname.replace(/\/+$/, '').split('/').filter(Boolean);
  const localisedRouteSegments = localisedRouteName.replace(/\/+$/, '').split('/').filter(Boolean);

  if (routeSegments.length !== localisedRouteSegments.length) return false;

  /*
   * First condition will always return true for the route pattern with dynamic params such as `:id`
   * Second condition will check if the pathname match the route pattern
   * For example, this will work:
   * - localisedRouteSegments = ['intent', ':intentType', ':intentId', 'confirmation']
   * - routeSegments = ['intent', 'setup', 'seti_1PgqJ8BBURfSZNfgTVQZrWGs', 'confirmation']
   */
  return localisedRouteSegments.every((pattern, i) => pattern.startsWith(':') || pattern === routeSegments[i]);
};

type GetScreenNameForTrackingByLocationPathnameParams = {
  pathname: string;
  universeReference?: UniverseReference;
};

export const getScreenNameForTrackingByLocationPathname = ({
  pathname,
  universeReference,
}: GetScreenNameForTrackingByLocationPathnameParams) => {
  // Here we remove query params
  const url = pathname.includes('?') ? pathname.substring(0, pathname.lastIndexOf('?')) : pathname;
  const screenName = Object.keys(ROUTES).find((key) => ROUTES[key] === url);

  if (screenName) {
    return toPascalCase(screenName);
  }

  // Workaround to correctly track the wellness prestations screen name as `Prestations`
  // Should be removed when dynamic URL params are correctly defined for the prestations route (see steps.tsx)
  if (isWellnessReference(universeReference) && url.startsWith(`${route(PRESTATIONS_PATH)}/`)) {
    return toPascalCase(PRESTATIONS_PATH);
  }

  const screenNameWithVar = Object.keys(ROUTES).find((name) => matchRouteWithURLParams(url, ROUTES[name]));
  return screenNameWithVar ? toPascalCase(screenNameWithVar) : pathname;
};

export const generateHash = (value: string) => sha256(value);

/**
 * In production, the Rails app will initialize Intercom with an included script.
 * For local development, the index.html file is including the same script with a test id.
 * In both cases Intercom is initialized globally and set to window.Intercom.
 */

export const accountCreated = () => {
  getGtmDataLayer().push({ event: 'AccountCreated' });
  Amplitude.track('AccountCreated');
};

type BookingCreatedParams = {
  booking: PayloadPotential;
  path: string;
  user: User;
};

export const bookingCreated = ({ booking, path, user }: BookingCreatedParams) => {
  const {
    universe,
    total_price: bookingTotalPrice,
    pro_price_base: proPriceBase,
    zoho_name: zohoName,
    country_code: countryCode,
    email,
    zone,
    gift_code: giftCode,
    gift_value: giftValue,
    user_first_potential: isFirstPotentialForUser,
    promotion,
    predicted_ltv: predictedLtv,
    has_membership: hasMembership,
    creation_type: creationType,
    phone,
    no_pro_reason: noProReason,
  } = booking;
  const totalPrice = bookingTotalPrice / 100;
  const revenue = (bookingTotalPrice - proPriceBase) / 100;

  const currentScreen = getScreenNameForTrackingByLocationPathname({
    pathname: path,
    universeReference: universe,
  });
  const locale = getI18nLocale();

  const serviceReference = isCleaningReference(universe)
    ? extractCleaningPrestationAndOptionsFromBooking(booking).cleaningPrestation?.reference
    : undefined;

  window.Intercom('trackEvent', BOOKING_CREATED_ALL, bookingMetaData(booking));
  window.Intercom('trackEvent', composeEvent(BOOKING_CREATED, booking), bookingMetaData(booking));

  if (!isWellnessReference(universe)) {
    getGtmDataLayer().push({
      event: 'PotentialConfirmation',
      potentialName: zohoName,
      price: totalPrice,
      revenue,
      universe,
      country: countryCode,
      recurrence: isRecurringChildcare(booking) || isRecurringCleaning(booking),
      service: serviceReference,
      screen: toPascalCase(currentScreen),
      mode: 'creation',
      source: 'website',
      zone,
      giftCard: !!giftCode && !!giftValue,
      promoCode: !!promotion && !!promotion.code && !!promotion.amount,
      creationType,
      userPublicId: user.public_id,
      // This hash is required in accordance with TikTok's data processing requirements.
      userEmail: generateHash(email),
      userPhone: generateHash(phone),
      predicted_ltv: predictedLtv,
      membership: user?.membership?.status,
      frequenceType: isFirstPotentialForUser ? 'first' : 'reorder',
      ...(isRecurringChildcare(booking) && { eligible: noProReason ? `false_${noProReason}` : true }),
    });

    Amplitude.track('PotentialConfirmation', {
      universe,
      source: 'website',
      country: countryCode,
      locale: formatLocaleToPreciseLocale({ locale, country: countryCode }),
      recurrence: isRecurringChildcare(booking) || isRecurringCleaning(booking),
      price: totalPrice,
      promoCode: !!promotion && !!promotion.code && !!promotion.amount,
      giftCard: !!giftCode && !!giftValue,
      creationType,
      zone,
      userPublicId: user.public_id,
      ...(isRecurringChildcare(booking) && { eligible: noProReason ? `false_${noProReason}` : true }),
    });

    if (isCleaningReference(universe)) {
      getGtmDataLayer().push({
        event: isRecurringCleaning(booking)
          ? 'PotentialConfirmationCleaningRecurrent'
          : 'PotentialConfirmationCleaningSpot',
        potentialName: zohoName,
        price: totalPrice,
        revenue,
        universe,
        country: countryCode,
        userEmail: generateHash(email),
        mode: 'creation',
        source: 'website',
      });
    }

    if (isFirstPotentialForUser) {
      const eventData = {
        predicted_ltv: predictedLtv,
        currency: getCurrentCurrency(),
        country: countryCode,
        userEmail: generateHash(email),
        membership: hasMembership,
        mode: 'creation',
        source: 'website',
      };

      getGtmDataLayer().push({
        event: 'NewUserCreated',
        service: serviceReference,
        ...eventData,
      });

      Amplitude.logEvent('NewUserCreated', {
        universe,
        source: 'website',
        country: countryCode,
        locale: formatLocaleToPreciseLocale({ locale, country: countryCode }),
        recurrence: isRecurringChildcare(booking) || isRecurringCleaning(booking),
        price: totalPrice,
        promoCode: !!promotion && !!promotion.code && !!promotion.amount,
        giftCard: !!giftCode && !!giftValue,
        creationType,
        zone,
        userPublicId: user.public_id,
        predicted_ltv: predictedLtv,
      });

      if (isCleaningReference(universe)) {
        getGtmDataLayer().push({
          event: isRecurringCleaning(booking) ? 'NewUserCleaningRecurrent' : 'NewUserCleaningSpot',
          ...eventData,
        });

        Amplitude.logEvent(isRecurringCleaning(booking) ? 'NewUserCleaningRecurrent' : 'NewUserCleaningSpot', {
          universe,
          source: 'website',
          country: countryCode,
          locale: formatLocaleToPreciseLocale({ locale, country: countryCode }),
          recurrence: isRecurringChildcare(booking) || isRecurringCleaning(booking),
          price: totalPrice,
          promoCode: !!promotion && !!promotion.code && !!promotion.amount,
          giftCard: !!giftCode && !!giftValue,
          creationType,
          zone,
          userPublicId: user.public_id,
        });
      }

      if (isChildcareReference(universe)) {
        getGtmDataLayer().push({
          event: isRecurringChildcare(booking) ? 'NewUserChildcareRecurrent' : 'NewUserChildcareSpot',
          ...eventData,
        });
      }
    }
  }
};

export const paymentPage = (universe: UniverseReference, isRecurring: boolean) => {
  window.Intercom('trackEvent', composeEventByUniverse(PAYMENT_PAGE, universe, isRecurring));
  getGtmDataLayer().push({ event: 'PaymentPage' });
};

type SummaryPageArgs = {
  isRecurring: boolean;
  universe: UniverseReference;
  user: User;
};

export const summaryPage = ({ isRecurring, universe, user }: SummaryPageArgs) => {
  const eventData = {
    last_visited_universe: universe,
  };
  window.Intercom('trackEvent', composeEventByUniverse(SUMMARY_PAGE, universe, isRecurring));
  // @ts-ignore custom attributes are not defined in intercom type
  window.Intercom('update', { ...eventData, intercom_user_jwt: user.intercom_user_jwt });
  getGtmDataLayer().push({ event: 'SummaryPage', ...eventData });
};

export const childcarePrestationsPage = () => {
  window.Intercom('trackEvent', CHILDCARE_PRESTATIONS_PAGE);
};

export const signInFunnel = (universe: UniverseReference, from: string) => {
  getGtmDataLayer().push({
    event: 'LoginFunnel',
    universe,
    step: from,
  });
};

type PreviousProsTrackingArgs = {
  userId?: string;
  numberOfPros: number;
  numberOfSelectedPros?: number;
  universe: UniverseReference;
};

type ProSelectArgs = PreviousProsTrackingArgs & {
  hasPreviousPro: boolean;
  proPosition: number;
  proPublicId: string;
  isOn: boolean;
  selectedProsSource: ProChoiceSource[];
};

export const proSelect = ({
  hasPreviousPro,
  userId,
  numberOfPros,
  numberOfSelectedPros,
  universe,
  proPosition,
  proPublicId,
  selectedProsSource,
  isOn,
}: ProSelectArgs) => {
  Amplitude.track('ProSelect', {
    hasPreviousPro,
    screen: toPascalCase(PREVIOUS_PROS_PATH),
    source: 'WebFunnel',
    userPublicId: userId,
    numberOfPros,
    numberOfSelectedPros,
    universe,
    proPosition,
    proPublicId,
    selectedProsSource: getSourcesFormatted(selectedProsSource),
    isOn,
    // Previous pros are only in creation mode
    mode: 'creation',
  });
};

type LetWecasaChooseCardClickParams = {
  universe: UniverseReference;
  userPublicId?: string | null;
};

export const letWecasaChooseCardClick = ({ universe, userPublicId }: LetWecasaChooseCardClickParams) => {
  const country = getCurrentCountry();
  Amplitude.track('LetWecasaChooseCardClick', {
    screen: toPascalCase(PREVIOUS_PROS_PATH),
    source: 'WebFunnel',
    userPublicId,
    universe,
    country,
    locale: formatLocaleToPreciseLocale({ locale: getI18nLocale(), country }),
    // Previous pros are only in creation mode
    mode: 'creation',
  });
};

type InstantTaxCreditProps = {
  universe: UniverseReference;
  userId?: string;
};

export const instantTaxCreditSwitchClick = ({
  isSwitchTurnedOn,
  universe,
  userId,
}: InstantTaxCreditProps & { isSwitchTurnedOn: boolean }) => {
  Amplitude.track('InstantTaxCreditSwitchClick', {
    screen: toPascalCase(RECAP_PATH),
    source: 'WebFunnel',
    turningOn: isSwitchTurnedOn,
    universe,
    userPublicId: userId,
    // Instant tax credit is only in creation mode
    mode: 'creation',
  });
};

export const instantTaxCreditTooltipClick = ({ universe, userId }: InstantTaxCreditProps) => {
  Amplitude.track('InstantTaxCreditTooltipClick', {
    screen: toPascalCase(RECAP_PATH),
    source: 'WebFunnel',
    universe,
    userPublicId: userId,
    // Instant tax credit is only in creation mode
    mode: 'creation',
  });
};

type TrackRecapNextButtonClickParams = {
  universe: UniverseReference;
  userId?: string;
  paymentMethod?: string;
};

export const trackRecapNextButtonClick = ({ paymentMethod, universe, userId }: TrackRecapNextButtonClickParams) => {
  const country = getCurrentCountry();

  Amplitude.track('NextButtonClick', {
    screen: toPascalCase(RECAP_PATH),
    source: 'WebFunnel',
    universe,
    userPublicId: userId,
    country,
    locale: formatLocaleToPreciseLocale({ locale: getI18nLocale(), country }),
    mode: 'creation',
    paymentMethod,
    // The Payment Element is the only payment flow on web
    isPaymentElementShown: true,
  });
};

type TrackRecapErrorParams = {
  universe: UniverseReference;
  userId?: string;
  paymentMethod?: string;
  formattedError: string;
};

export const trackRecapError = ({ formattedError, paymentMethod, universe, userId }: TrackRecapErrorParams) => {
  const country = getCurrentCountry();

  Amplitude.track('Error', {
    screen: toPascalCase(RECAP_PATH),
    source: 'WebFunnel',
    universe,
    userPublicId: userId,
    country,
    locale: formatLocaleToPreciseLocale({ locale: getI18nLocale(), country }),
    mode: 'creation',
    paymentMethod,
    // The Payment Element is the only payment flow on web
    isPaymentElementShown: true,
    errorMessage: formattedError,
  });
};

type TrackScreenViewArgs = {
  cleaningPrestation?: BasketState['cleaningPrestation'];
  mode: BasketMode;
  path: string;
  taxCreditChoice?: 'Instant' | 'Classic';
  universe: UniverseReference;
  user?: User;
  utmMedium?: string;
};

export const trackScreenView = ({
  cleaningPrestation,
  mode,
  path,
  taxCreditChoice,
  universe,
  user,
  utmMedium,
}: TrackScreenViewArgs) => {
  const service = isCleaningReference(universe) ? cleaningPrestation?.reference : undefined;
  const country = getCurrentCountry();

  getGtmDataLayer().push({
    event: 'ScreenView',
    userEmail: user ? generateHash(user.email) : null,
    userPublicId: user?.public_id ?? null,
    userPhone: user?.phone ? generateHash(user.phone) : null,
  });

  Amplitude.track(SCREEN_VIEW, {
    country,
    locale: formatLocaleToPreciseLocale({ locale: getI18nLocale(), country }),
    membershipCreatedAt: user?.membership?.created_at,
    membershipOrigin: user?.membership?.origin,
    membershipStatus: user?.membership?.status,
    mode,
    screen: getScreenNameForTrackingByLocationPathname({
      pathname: path,
      universeReference: universe,
    }),
    service,
    source: 'WebFunnel',
    universe,
    userPublicId: user?.public_id,
    ...(taxCreditChoice ? { taxCreditChoice } : {}),
    ...(utmMedium ? { utmMedium } : {}),
  });
};

type TrackSignInEventParams = {
  eventType: SignInEvent;
  path: string;
  universe: UniverseReference;
};

export const trackSignInEvent = ({ eventType, path, universe }: TrackSignInEventParams) => {
  Amplitude.track(eventType, {
    screen: getScreenNameForTrackingByLocationPathname({
      pathname: path,
      universeReference: universe,
    }),
    source: 'WebFunnel',
    universe,
    // Login is always in creation mode because modification mode is only accessible for authenticated users
    mode: 'creation',
  });
};

type PreferredProsModalArgs = {
  userId?: string;
  universe: UniverseReference;
  screen: string;
  numberOfProsSelected: number;
  mode: BasketMode;
};

export const switchToPreferredModalView = ({
  userId,
  universe,
  screen,
  numberOfProsSelected,
  mode,
}: PreferredProsModalArgs) => {
  Amplitude.track('SwitchToPreferredModalView', {
    screen: toPascalCase(screen),
    source: 'WebFunnel',
    userPublicId: userId,
    universe,
    numberOfProsSelected,
    mode,
  });
};

export const switchToPreferredModalClose = ({
  userId,
  universe,
  screen,
  numberOfProsSelected,
  mode,
}: PreferredProsModalArgs) => {
  Amplitude.track('SwitchToPreferredModalClose', {
    screen: toPascalCase(screen),
    source: 'WebFunnel',
    userPublicId: userId,
    universe,
    numberOfProsSelected,
    mode,
  });
};

export const switchToPreferredModalRefuse = ({
  userId,
  universe,
  screen,
  numberOfProsSelected,
  mode,
}: PreferredProsModalArgs) => {
  Amplitude.track('SwitchToPreferredModalRefuse', {
    screen: toPascalCase(screen),
    source: 'WebFunnel',
    userPublicId: userId,
    universe,
    numberOfProsSelected,
    mode,
  });
};

export const switchToPreferredModalAccept = ({
  userId,
  universe,
  screen,
  numberOfProsSelected,
  mode,
}: PreferredProsModalArgs) => {
  Amplitude.track('SwitchToPreferredModalAccept', {
    screen: toPascalCase(screen),
    source: 'WebFunnel',
    userPublicId: userId,
    universe,
    numberOfProsSelected,
    mode,
  });
};

export const trackITCForSomeoneElseAlertSee = (userId: string, mode: BasketMode) => {
  Amplitude.track('ITCForSomeoneElseAlertSee', {
    screen: toPascalCase(CUSTOMER_PATH),
    source: 'WebFunnel',
    universe: 'cleaning',
    userPublicId: userId,
    mode,
  });
};

type TrackLocationEventParams = {
  eventType: TrackLocationEvent;
  universe: UniverseReference;
  userPublicId?: string | null;
  mode: BasketMode;
};

export const trackLocationEvent = ({ eventType, universe, userPublicId, mode }: TrackLocationEventParams) => {
  const country = getCurrentCountry();
  Amplitude.track(eventType, {
    screen: 'AddressScreen',
    source: 'WebFunnel',
    userPublicId,
    universe,
    country,
    locale: formatLocaleToPreciseLocale({ locale: getI18nLocale(), country }),
    mode,
  });
};

type TrackNonManagedAddressModalDisplayParams = {
  universeReference: UniverseReference;
  mode: BasketMode;
};

export const trackNonManagedAddressModalDisplay = ({
  universeReference,
  mode,
}: TrackNonManagedAddressModalDisplayParams) => {
  const country = getCurrentCountry();
  Amplitude.track('NonManagedAddressModalDisplay', {
    screen: 'AddressScreen',
    source: 'WebFunnel',
    universe: universeReference,
    country,
    locale: formatLocaleToPreciseLocale({ locale: getI18nLocale(), country }),
    mode,
  });
};

type SameSessionModalTrackingArgs = {
  eventType: Extract<TrackingEventType, 'DuplicateAlertSee' | 'DuplicateAlertClose' | 'DuplicateAlertClick'>;
  universe: UniverseReference;
  userPublicId: string;
  path: string;
  mode: BasketMode;
  option?: 'noCancel' | 'keepGoing';
};

export const sameSessionModalTracking = ({
  eventType,
  option,
  universe,
  userPublicId,
  path,
  mode,
}: SameSessionModalTrackingArgs) => {
  const country = getCurrentCountry();
  Amplitude.track(eventType, {
    screen: getScreenNameForTrackingByLocationPathname({
      pathname: path,
      universeReference: universe,
    }),
    source: 'WebFunnel',
    userPublicId,
    universe,
    country,
    locale: formatLocaleToPreciseLocale({ locale: getI18nLocale(), country }),
    option,
    mode,
  });
};

type TrackAddPromoCodeArgs = {
  universe: UniverseReference;
  path: string;
  mode: BasketMode;
};

export const trackAddPromoCode = ({ universe, path, mode }: TrackAddPromoCodeArgs) => {
  const screenName = getScreenNameForTrackingByLocationPathname({
    pathname: path,
    universeReference: universe,
  });
  const country = getCurrentCountry();
  Amplitude.track('AddPromoCode', {
    screen: screenName,
    source: 'WebFunnel',
    universe,
    mode,
    country,
    locale: formatLocaleToPreciseLocale({ locale: getI18nLocale(), country }),
  });
};

type TrackOpenBasketArgs = {
  universe: UniverseReference;
  path: string;
  mode: BasketMode;
};

export const trackOpenBasket = ({ universe, path, mode }: TrackOpenBasketArgs) => {
  const screenName = getScreenNameForTrackingByLocationPathname({
    pathname: path,
    universeReference: universe,
  });
  const country = getCurrentCountry();
  Amplitude.track('OpenBasket', {
    screen: screenName,
    source: 'WebFunnel',
    universe,
    mode,
    country,
    locale: formatLocaleToPreciseLocale({ locale: getI18nLocale(), country }),
  });
};

type TrackMoreDetailsClickArgs = {
  universe: UniverseReference;
  path: string;
  mode: BasketMode;
};

export const trackMoreDetailsClick = ({ universe, path, mode }: TrackMoreDetailsClickArgs) => {
  const screenName = getScreenNameForTrackingByLocationPathname({
    pathname: path,
    universeReference: universe,
  });
  const country = getCurrentCountry();
  Amplitude.track('MoreDetailsClick', {
    screen: screenName,
    source: 'WebFunnel',
    universe,
    mode,
    country,
    locale: formatLocaleToPreciseLocale({ locale: getI18nLocale(), country }),
  });
};

type TrackCleaningEventParams = {
  path: Path;
  eventType: TrackingEventType;
  options: Record<string, string>;
  mode: BasketMode;
};

const trackCleaningEvent = ({ path, eventType, options, mode }: TrackCleaningEventParams) => {
  const country = getCurrentCountry();
  Amplitude.track(eventType, {
    country,
    locale: formatLocaleToPreciseLocale({ locale: getI18nLocale(), country }),
    screen: toPascalCase(path),
    source: 'WebFunnel',
    universe: 'cleaning',
    mode,
    ...options,
  });
};

export const trackCleaningServiceClick = (prestation: CleaningPrestation, mode: BasketMode) => {
  trackCleaningEvent({
    path: 'services',
    eventType: 'CleaningServiceClick',
    options: { service: prestation.reference },
    mode,
  });
};

export const sendCleaningPrestationToIntercom = (prestationReference: string | undefined, user?: User) => {
  const eventData = {
    cleaning_last_selected_service: prestationReference,
  };
  // @ts-ignore custom attributes are not defined in intercom type
  window.Intercom('update', {
    ...eventData,
    ...(user?.intercom_user_jwt ? { intercom_user_jwt: user.intercom_user_jwt } : {}),
  });
};

export const trackCleaningOptionClick = (option: CleaningPrestationOption, mode: BasketMode) => {
  trackCleaningEvent({
    path: 'options',
    eventType: 'CleaningOptionClick',
    options: { option: option.reference },
    mode,
  });
};

export const trackCleaningOptionInfoClick = (option: CleaningPrestationOption, mode: BasketMode) => {
  trackCleaningEvent({
    path: 'options',
    eventType: 'CleaningOptionInfoClick',
    options: { option: option.reference },
    mode,
  });
};

export const trackLaunchResearchClick = (user?: User) => {
  const country = getCurrentCountry();
  Amplitude.track('LaunchResearchClick', {
    screen: toPascalCase(SUMMARY_PATH),
    source: 'WebFunnel',
    membershipStatus: user?.membership?.status,
    membershipOrigin: user?.membership?.origin,
    membershipCreatedAt: user?.membership?.created_at,
    universe: 'childcare',
    mode: 'creation',
    country,
    locale: formatLocaleToPreciseLocale({ locale: getI18nLocale(), country }),
    userPublicId: user?.public_id,
  });
};

export const trackBookMeetingClick = (user: User) => {
  const country = getCurrentCountry();
  Amplitude.track('BookMeetingClick', {
    screen: toPascalCase(SUMMARY_PATH),
    source: 'WebFunnel',
    membershipStatus: user?.membership?.status,
    membershipOrigin: user?.membership?.origin,
    membershipCreatedAt: user?.membership?.created_at,
    universe: 'childcare',
    mode: 'creation',
    country,
    locale: formatLocaleToPreciseLocale({ locale: getI18nLocale(), country }),
    userPublicId: user?.public_id,
  });
};

type TrackChildcareAllowanceArgs = {
  user?: User;
  mode: BasketMode;
};

export const trackChildcareAllowanceScreen = ({ user, mode }: TrackChildcareAllowanceArgs) => {
  const country = getCurrentCountry();
  Amplitude.track('CafAllowanceScreen', {
    screen: 'ChildcareAllowanceScreen',
    source: 'WebFunnel',
    userPublicId: user?.public_id,
    membershipStatus: user?.membership?.status,
    membershipOrigin: user?.membership?.origin,
    membershipCreatedAt: user?.membership?.created_at,
    universe: 'childcare',
    mode,
    country,
    locale: formatLocaleToPreciseLocale({ locale: getI18nLocale(), country }),
  });
};

type TrackChildcareCallMeBackArgs = {
  mode: BasketMode;
  user: User;
};

export const trackChildcareCallMeBackClick = ({ mode, user }: TrackChildcareCallMeBackArgs) => {
  getGtmDataLayer().push({
    event: 'LogEvent',
    eventType: 'CallMeBackClick',
    eventProperties: {
      country: getCurrentCountry(),
      membershipCreatedAt: user.membership?.created_at,
      membershipOrigin: user.membership?.origin,
      membershipStatus: user.membership?.status,
      mode,
      screen: 'ChildcareRecurrentSummary',
      source: 'WebFunnel',
      universe: 'childcare',
      userPublicId: user.public_id,
    },
  });
};

export const logWoosmapErrorIncompleteAddress = () => {
  Amplitude.track('WoosmapErrorIncompleteAddress');
};

type TrackCheckoutConfirmationEventParams = {
  eventType: CheckoutConfirmationEvent;
  userPublicId: string;
  universeReference?: UniverseReference;
  errorType?: 'payment_error' | 'potential_error' | 'unknown_error';
  errorMessage?: string;
};

export const trackCheckoutConfirmationEvent = ({
  eventType,
  userPublicId,
  universeReference,
  errorType,
  errorMessage,
}: TrackCheckoutConfirmationEventParams) => {
  const country = getCurrentCountry();

  Amplitude.track(eventType, {
    screen: toPascalCase(CHECKOUT_CONFIRMATION_PATH),
    source: 'WebFunnel',
    universe: universeReference,
    userPublicId,
    country,
    locale: formatLocaleToPreciseLocale({ locale: getI18nLocale(), country }),
    // Screen is only available in creation mode
    mode: 'creation',
    errorType,
    errorMessage,
  });
};
